import { Badge } from '../../ui/primitives/Badge.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { LiveRegion } from '../../ui/primitives/LiveRegion.tsx';
import { Surface } from '../../ui/primitives/Surface.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './SubmissionFailure.module.css';

interface SubmissionFailureProps {
  /** Resends the answer the learner already gave; never asks for a new one. */
  onRetry: () => void;
  /** True while a retry is in flight, so it cannot be fired twice. */
  retrying: boolean;
}

/**
 * FR-055, FR-065 (T074) — a submission that did not commit is SAID SO.
 *
 * The whole of FR-055 is that a 2xx from submit means the row is committed and
 * nothing else does. So a failure here has exactly one permitted presentation:
 * say the answer was not recorded, and offer to send it again. What it must
 * never do is the thing that would be easiest — render feedback anyway,
 * optimistically, and reconcile later. A learner told "correct" for an attempt
 * that does not exist has been lied to about the only record this app keeps.
 *
 * Three consequences visible in this component:
 *
 *  - No result is shown. Not a provisional one, not a greyed-out one. The
 *    shell keeps `phase` at 'answering' and never sets `feedback`, so there is
 *    no correct/incorrect anywhere on screen while this is up.
 *  - Retry resends the SAME response (`lastResponseRef` in the shell). The
 *    learner answered once; being asked to answer again would imply their
 *    first answer was at fault, and it also risks a different answer being
 *    recorded than the one they gave.
 *  - It is announced (FR-065). A failure a screen-reader user never hears is
 *    a silent failure, which is precisely what FR-055 forbids.
 *
 * Retrying is safe against duplication regardless of what actually happened on
 * the server: if the first attempt did commit and only the response was lost,
 * the retry lands on the already-submitted path and returns `409` carrying the
 * ORIGINAL result, so the learner ends up with one recorded attempt either way
 * (contracts/learner-api.md; attempts.service.ts).
 */
export function SubmissionFailure({ onRetry, retrying }: SubmissionFailureProps) {
  return (
    <Surface as="section" layer={2} radius="lg" inset>
      <div className={styles.failure} data-testid="submit-failure">
        <Badge tone="near-miss" icon="alert-circle">
          Não enviado
        </Badge>
        <Text as="p" variant="body" tone="error">
          Sua resposta não foi registrada.
        </Text>
        <Text as="p" variant="body-sm" tone="muted">
          Verifique sua conexão e envie novamente. Sua resposta foi guardada aqui — você não
          precisa escolher de novo.
        </Text>
        {/*
          Two renderings rather than a `disabled={retrying}` prop: `Button`'s
          props are a discriminated union in which `disabled` is literally
          `true` and always arrives with its reason (Button.tsx), so a
          disabled control can never reach the DOM without one — § 12's rule
          expressed as a type rather than as a convention.
        */}
        <span data-testid="submit-retry">
          {retrying ? (
            <Button id="submit-retry" icon="rotate-ccw" disabled disabledReason="Enviando sua resposta.">
              Enviando…
            </Button>
          ) : (
            <Button id="submit-retry" icon="rotate-ccw" onClick={onRetry}>
              Enviar novamente
            </Button>
          )}
        </span>
        {/*
          FR-065 — polite, so it does not interrupt whatever the learner is
          reading, but it does reach assistive technology. The message is the
          same sentence shown above: one statement of what happened.
        */}
        <LiveRegion message="Sua resposta não foi registrada. Envie novamente." />
      </div>
    </Surface>
  );
}
